import { isQueueable, isNetworkError, enqueue, flushQueue } from './lib/offline';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function getToken() {
  return localStorage.getItem('muscuapp_token');
}

async function rawFetch(path, { method = 'GET', body, headers } = {}) {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) {
    localStorage.removeItem('muscuapp_token');
    localStorage.removeItem('muscuapp_user');
    localStorage.removeItem('muscuapp_role');
    window.location.replace('/');
    throw new Error('Session expirée');
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || res.statusText);
  }
  return res.json();
}

async function apiFetch(path, options = {}) {
  const method = options.method || 'GET';
  try {
    return await rawFetch(path, options);
  } catch (err) {
    // Hors ligne : les écritures de séance sont mises en file et rejouées au retour du réseau
    if (isNetworkError(err) && isQueueable(method, path)) {
      enqueue({ path, method, body: options.body });
      return { queued: true };
    }
    throw err;
  }
}

export const syncQueue = () => flushQueue((entry) => rawFetch(entry.path, entry));

async function backupFetch(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { Authorization: `Bearer ${getToken()}`, ...options.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || res.statusText);
  }
  return res;
}

export const api = {
  // Sauvegarde (admin)
  downloadBackup: async () => {
    const res = await backupFetch('/backup/download');
    const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') || '')?.[1] || 'muscuapp-backup.db';
    return { blob: await res.blob(), name };
  },
  restoreBackup: (file) =>
    backupFetch('/backup/restore', { method: 'POST', headers: { 'Content-Type': 'application/octet-stream' }, body: file }).then((r) => r.json()),

  // Auth
  login: (username, password) =>
    apiFetch('/auth/login', { method: 'POST', body: { username, password } }),

  changePassword: (current_password, new_password) =>
    apiFetch('/auth/change-password', { method: 'POST', body: { current_password, new_password } }),

  // Users (admin)
  getUsers: () => apiFetch('/users'),
  createUser: (data) => apiFetch('/users', { method: 'POST', body: data }),
  updateUser: (id, data) => apiFetch(`/users/${id}`, { method: 'PUT', body: data }),
  deleteUser: (id) => apiFetch(`/users/${id}`, { method: 'DELETE' }),

  // Exercises
  getExercises: () => apiFetch('/exercises'),
  getExercisesByDay: (day) => apiFetch(`/exercises/day/${encodeURIComponent(day)}`),
  createExercise: (data) => apiFetch('/exercises', { method: 'POST', body: data }),
  updateExercise: (id, data) => apiFetch(`/exercises/${id}`, { method: 'PUT', body: data }),
  deleteExercise: (id) => apiFetch(`/exercises/${id}`, { method: 'DELETE' }),

  reorderExercises: (ids) => apiFetch('/exercises/reorder', { method: 'POST', body: { ids } }),
  substituteExercise: (id, libraryId) => apiFetch(`/exercises/${id}/substitute`, { method: 'POST', body: { library_id: libraryId } }),
  makeMain: (id) => apiFetch(`/exercises/${id}/make-main`, { method: 'POST' }),

  // Programs
  getPrograms: () => apiFetch('/programs'),
  getProgram: (id) => apiFetch(`/programs/${id}`),
  createProgram: (data) => apiFetch('/programs', { method: 'POST', body: data }),
  updateProgram: (id, data) => apiFetch(`/programs/${id}`, { method: 'PUT', body: data }),
  deleteProgram: (id) => apiFetch(`/programs/${id}`, { method: 'DELETE' }),
  duplicateProgram: (id) => apiFetch(`/programs/${id}/duplicate`, { method: 'POST' }),
  setProgramFavorite: (id, favorite) => apiFetch(`/programs/${id}/favorite`, { method: 'PUT', body: { favorite } }),
  reorderPrograms: (ids) => apiFetch('/programs/reorder', { method: 'POST', body: { ids } }),

  // Library
  getLibrary: () => apiFetch('/library'),
  getLibraryItem: (id) => apiFetch(`/library/${id}`),
  createLibraryItem: (data) => apiFetch('/library', { method: 'POST', body: data }),
  updateLibraryItem: (id, data) => apiFetch(`/library/${id}`, { method: 'PUT', body: data }),
  deleteLibraryItem: (id) => apiFetch(`/library/${id}`, { method: 'DELETE' }),
  deleteLibraryMedia: (id, kind) => apiFetch(`/library/${id}/media?kind=${kind}`, { method: 'DELETE' }),
  uploadLibraryMedia: async (id, kind, file) => {
    const res = await fetch(`${API_BASE}/library/${id}/media?kind=${kind}`, {
      method: 'POST',
      headers: {
        'Content-Type': file.type,
        Authorization: 'Bearer ' + getToken(),
      },
      body: file,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || res.statusText);
    }
    return res.json();
  },

  getAchievements: () => apiFetch('/achievements'),

  // Sessions
  getSessions: () => apiFetch('/sessions'),
  getSession: (id) => apiFetch(`/sessions/${id}`),
  createSession: (data) => apiFetch('/sessions', { method: 'POST', body: data }),
  deleteSession: (id) => apiFetch(`/sessions/${id}`, { method: 'DELETE' }),
  updateSession: (id, data) => apiFetch(`/sessions/${id}`, { method: 'PUT', body: data }),
  saveSets: (sessionId, sets) =>
    apiFetch(`/sessions/${sessionId}/sets`, { method: 'POST', body: { sets } }),
  skipExercise: (sessionId, exerciseId) =>
    apiFetch(`/sessions/${sessionId}/skip`, { method: 'POST', body: { exercise_id: exerciseId } }),
  unskipExercise: (sessionId, exerciseId) =>
    apiFetch(`/sessions/${sessionId}/skip/${exerciseId}`, { method: 'DELETE' }),
  addFreeExercise: (sessionId, libraryId) =>
    apiFetch(`/sessions/${sessionId}/exercises`, { method: 'POST', body: { library_id: libraryId } }),
  removeFreeExercise: (sessionId, exerciseId) =>
    apiFetch(`/sessions/${sessionId}/exercises/${exerciseId}`, { method: 'DELETE' }),
  reorderFreeExercises: (sessionId, ids) =>
    apiFetch(`/sessions/${sessionId}/exercises/reorder`, { method: 'POST', body: { ids } }),
  deleteSet: (sessionId, setId) =>
    apiFetch(`/sessions/${sessionId}/sets/${setId}`, { method: 'DELETE' }),
  getLastPerformance: (exerciseIds, excludeSessionId) => {
    const params = new URLSearchParams({ exerciseIds: exerciseIds.join(',') });
    if (excludeSessionId) params.set('excludeSessionId', excludeSessionId);
    return apiFetch(`/sessions/last-performance?${params}`);
  },

  // Stats
  getExerciseStats: (exerciseId) => apiFetch(`/stats/exercise/${exerciseId}`),
  getOverview: () => apiFetch('/stats/overview'),
  getBests: () => apiFetch('/stats/bests'),
  getMuscleWeekly: (weeks) => apiFetch(`/stats/muscle-weekly${weeks ? '?weeks=' + weeks : ''}`),
  getGroupVolume: (days) => apiFetch(`/stats/group-volume${days ? '?days=' + days : ''}`),

  // Suivi corporel
  getBody: () => apiFetch('/body'),
  createBody: (data) => apiFetch('/body', { method: 'POST', body: data }),
  updateBody: (id, data) => apiFetch(`/body/${id}`, { method: 'PUT', body: data }),
  deleteBody: (id) => apiFetch(`/body/${id}`, { method: 'DELETE' }),

  // Planning hebdomadaire
  getSchedule: () => apiFetch('/schedule'),
  saveSchedule: (days) => apiFetch('/schedule', { method: 'PUT', body: { days } }),
};
